"use client";

import React, { useState } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import {
  CheckCircle,
  AlertCircle,
  Loader2,
  ArrowRight,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { useUserActions } from "@/hooks/useUserActions";
import { handleError } from "@/utils/errorHandler";
import MarketingNavBar from "@/components/MarketingNavBar";
import { isFacebookUrl, isGoogleMapsUrl, isWebUrl, normalizeUrl } from "@/utils/urlValidation";

interface PreRegistrationFormData {
  email: string;
  phoneNumber: string;
  address: string;
  facebookPage: string;
  website: string;
  googleMapsLink: string;
}

interface FormErrors {
  email?: string;
  phoneNumber?: string;
  website?: string;
  facebookPage?: string;
  googleMapsLink?: string;
}

const inputCls =
  "w-full bg-gray-900 border border-gray-700 rounded-lg px-3.5 py-2.5 text-white text-[0.92rem] placeholder-gray-500 transition-all duration-150 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:bg-[#0f1623]";

const PreRegistrationPage: React.FC = () => {
  const t = useTranslations("PreRegistration");
  const tErrors = useTranslations("PreRegistration.Errors");
  const { submitPreRegistration } = useUserActions();
  const [formData, setFormData] = useState<PreRegistrationFormData>({
    email: "",
    phoneNumber: "",
    address: "",
    facebookPage: "",
    website: "",
    googleMapsLink: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    success?: boolean;
    message?: string;
  }>({});
  const [globalError, setGlobalError] = useState<string | null>(null);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.email) {
      newErrors.email = t("Errors.EmailRequired");
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t("Errors.InvalidEmail");
    }

    if (formData.phoneNumber && !/^[\+]?[0-9\s\-\(\)]{8,}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = t("Errors.InvalidPhone");
    }

    if (formData.website && !isWebUrl(formData.website)) {
      newErrors.website = t("Errors.InvalidWebsite");
    }

    if (formData.facebookPage && !isFacebookUrl(formData.facebookPage)) {
      newErrors.facebookPage = t("Errors.InvalidFacebook");
    }

    // Same defect the athlete form had: this rejected the maps.app.goo.gl
    // shortlink the phone share sheet produces, which is what people paste.
    if (formData.googleMapsLink && !isGoogleMapsUrl(formData.googleMapsLink)) {
      newErrors.googleMapsLink = t("Errors.InvalidGoogleMaps");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus({});
    setGlobalError(null);

    await submitPreRegistration(
      {
        emailAddress: formData.email,
        phoneNumber: formData.phoneNumber || null,
        address: formData.address,
        website: normalizeUrl(formData.website),
        facebookPage: normalizeUrl(formData.facebookPage),
        googleMapsLink: normalizeUrl(formData.googleMapsLink),
        // This form is the venue's own operator asking to be set up, as opposed
        // to an athlete recommending a venue from /browse.
        source: "OWNER_SIGNUP",
      },
      {
        onSuccess: () => {
          setSubmitStatus({ success: true, message: t("SuccessMessage") });
          setFormData({ email: "", phoneNumber: "", address: "", facebookPage: "", website: "", googleMapsLink: "" });
          setIsSubmitting(false);
        },
        onError: (err) => {
          let userMessage = tErrors("SubmissionFailed");
          if (err.details?.errorCode === "EMAIL_ALREADY_EXISTS") {
            userMessage = tErrors("EmailAlreadyExists");
          }
          setSubmitStatus({ success: false, message: userMessage });
          handleError(err, { component: "PreRegistrationPage" }, setGlobalError, userMessage);
          setIsSubmitting(false);
        },
      }
    );
  };

  return (
    <div className="min-h-screen bg-[#0f1623] text-white relative">
      {/* Subtle top vignette — depth without colored orbs */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(60% 50% at 50% -10%, rgba(59,130,246,0.07), transparent 70%)" }}
      />

      <MarketingNavBar />

      <main className="relative max-w-6xl mx-auto px-6 pt-20 pb-28">
        <div className="grid lg:grid-cols-[1fr_1.05fr] gap-14 lg:gap-20 items-start">

          {/* LEFT: pitch */}
          <div className="lg:sticky lg:top-24">
            <p className="text-[0.72rem] tracking-[0.16em] uppercase font-semibold text-blue-400 mb-5">
              {t("TrackerLabel")}
            </p>

            <h1 className="text-[clamp(2.4rem,4.4vw,3.6rem)] font-extrabold leading-[1.05] tracking-[-0.025em] mb-5">
              {t("TitleLine1")}<br />
              <span className="text-blue-400">{t("TitleLine2")}</span>
            </h1>

            <p className="text-gray-300 text-[1.02rem] leading-relaxed mb-9 max-w-md">
              {t("Description")}
            </p>

            {/* Beta benefits — quiet checklist */}
            <div className="mb-9">
              <p className="text-[0.72rem] tracking-[0.16em] uppercase font-semibold text-gray-500 mb-4">
                {t("WhatYouGetInBeta")}
              </p>
              <ul className="space-y-3.5 text-[0.95rem]">
                {[
                  { bold: t("BetaBanner.FreeBetaBold"), rest: " " + t("BetaBanner.FreeBetaRest") },
                  { bold: t("BetaBanner.EarlyAccessBold"), rest: " " + t("BetaBanner.EarlyAccessRest") },
                ].map((item) => (
                  <li key={item.bold} className="flex items-start gap-3">
                    <span
                      className="mt-[3px] w-[18px] h-[18px] rounded-full flex-shrink-0 inline-flex items-center justify-center"
                      style={{ background: "rgba(52,211,153,0.12)", color: "#34D399" }}
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    <span className="text-gray-200">
                      <span className="text-white font-medium">{item.bold}</span>
                      {item.rest}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-xs text-gray-500 leading-relaxed max-w-sm">
              {t("AlreadyOnAtlivo")}{" "}
              <Link href="/sign-up" className="text-blue-400 hover:text-blue-300 underline-offset-2 hover:underline">
                {t("SignIn")}
              </Link>{" "}
              {t("SignInSuffix")}
            </p>
          </div>

          {/* RIGHT: form card */}
          <div>
            {submitStatus.success ? (
              <motion.div
                className="bg-gray-800 border border-gray-700/60 rounded-2xl p-9 shadow-[0_25px_60px_-24px_rgba(0,0,0,0.7)] text-center py-16"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
              >
                <CheckCircle className="w-14 h-14 text-emerald-400 mx-auto mb-6" />
                <h3 className="text-2xl font-bold text-white mb-3">{t("SuccessTitle")}</h3>
                <p className="text-gray-300">{submitStatus.message}</p>
              </motion.div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-gray-800 border border-gray-700/60 rounded-2xl p-7 sm:p-9 shadow-[0_25px_60px_-24px_rgba(0,0,0,0.7)]"
              >
                {/* CONTACT */}
                <div>
                  <p className="text-[0.72rem] tracking-[0.16em] uppercase font-semibold text-gray-500 mb-5">
                    {t("Form.ContactSection")}
                  </p>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[0.78rem] font-medium text-gray-300 mb-2">
                        {t("Form.EmailLabel")} <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder={t("Form.EmailPlaceholder")}
                        className={inputCls}
                        required
                      />
                      {errors.email && (
                        <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5" />
                          {errors.email}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-[0.78rem] font-medium text-gray-300 mb-2">
                        {t("Form.PhoneLabel")}{" "}
                        <span className="text-[0.7rem] text-gray-500 font-normal">({t("Form.Optional")})</span>
                      </label>
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        placeholder={t("Form.PhonePlaceholder")}
                        className={inputCls}
                      />
                      {errors.phoneNumber && (
                        <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5" />
                          {errors.phoneNumber}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="my-8 border-t border-gray-600/40" />

                {/* BUSINESS */}
                <div>
                  <p className="text-[0.72rem] tracking-[0.16em] uppercase font-semibold text-gray-500 mb-5">
                    {t("Form.BusinessSection")}
                  </p>
                  <div>
                    <label className="block text-[0.78rem] font-medium text-gray-300 mb-2">
                      {t("Form.AddressLabel")}{" "}
                      <span className="text-[0.7rem] text-gray-500 font-normal">({t("Form.Optional")})</span>
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder={t("Form.AddressPlaceholder")}
                      className={inputCls}
                    />
                  </div>
                </div>

                <div className="my-8 border-t border-gray-600/40" />

                {/* ONLINE PRESENCE */}
                <div>
                  <p className="text-[0.72rem] tracking-[0.16em] uppercase font-semibold text-gray-500 mb-5">
                    {t("Form.OnlinePresenceSection")}
                  </p>
                  <div className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-[0.78rem] font-medium text-gray-300 mb-2">
                          {t("Form.WebsiteLabel")}{" "}
                          <span className="text-[0.7rem] text-gray-500 font-normal">({t("Form.Optional")})</span>
                        </label>
                        <input
                          type="text"
                          inputMode="url"
                          name="website"
                          value={formData.website}
                          onChange={handleInputChange}
                          placeholder={t("Form.WebsitePlaceholder")}
                          className={inputCls}
                        />
                        {errors.website && (
                          <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                            <AlertCircle className="w-3.5 h-3.5" />
                            {errors.website}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-[0.78rem] font-medium text-gray-300 mb-2">
                          {t("Form.FacebookLabel")}{" "}
                          <span className="text-[0.7rem] text-gray-500 font-normal">({t("Form.Optional")})</span>
                        </label>
                        <input
                          type="text"
                          inputMode="url"
                          name="facebookPage"
                          value={formData.facebookPage}
                          onChange={handleInputChange}
                          placeholder={t("Form.FacebookPlaceholder")}
                          className={inputCls}
                        />
                        {errors.facebookPage && (
                          <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                            <AlertCircle className="w-3.5 h-3.5" />
                            {errors.facebookPage}
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-[0.78rem] font-medium text-gray-300 mb-2">
                        {t("Form.GoogleMapsLabel")}{" "}
                        <span className="text-[0.7rem] text-gray-500 font-normal">({t("Form.Optional")})</span>
                      </label>
                      <input
                        type="text"
                        inputMode="url"
                        name="googleMapsLink"
                        value={formData.googleMapsLink}
                        onChange={handleInputChange}
                        placeholder={t("Form.GoogleMapsPlaceholder")}
                        className={inputCls}
                      />
                      <p className="mt-1.5 text-[0.72rem] text-gray-500">{t("Form.GoogleMapsDescription")}</p>
                      {errors.googleMapsLink && (
                        <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5" />
                          {errors.googleMapsLink}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Consent */}
                <p className="text-xs text-gray-500 leading-relaxed mt-8 mb-7">
                  {t("ConsentNotice")}
                </p>

                {/* Error states */}
                {submitStatus.success === false && (
                  <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-red-400 font-medium text-sm">{t("Errors.ErrorTitle")}</p>
                      <p className="text-red-300 text-sm mt-0.5">{submitStatus.message}</p>
                      <p className="text-xs text-gray-400 mt-2">
                        <Link href="/contact" className="text-blue-400 hover:text-blue-300 underline">
                          {t("Errors.ContactSupportLink")}
                        </Link>
                      </p>
                    </div>
                  </div>
                )}
                {globalError && (
                  <div className="mb-6 bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start gap-3">
                    <AlertCircle className="w-4.5 h-4.5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-red-400 font-medium text-sm">{tErrors("ErrorTitle")}</p>
                      <p className="text-xs text-red-300 mt-0.5">{globalError}</p>
                    </div>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 active:translate-y-px disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-[0.6rem] py-4 text-[1rem] transition-all duration-150"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t("Form.Submitting")}
                    </>
                  ) : (
                    <>
                      {t("Form.SubmitButton")}
                      <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

        </div>
      </main>
    </div>
  );
};

export default PreRegistrationPage;
